import React, { useMemo } from 'react';
import './ReviewProjectInfoModal.css';
import {
  SharedFieldSettings,
  WarmIntroShareFieldKey,
  countHiddenRecommendedFields,
} from '../lib/warmIntroSharing';
import { getPrimaryCapabilityLabel } from '../constants';

interface MatchingFilters {
  productType: string[];
  detailedServices: string[];
  certifications: string[];
  cdmoPartnerType: string;
  developmentStage: string;
  supportType: string[];
  batchSize: string;
  speedToStart: string;
  budget: string;
  location: string[];
}

export interface ReviewProjectInfoModalProps {
  isOpen: boolean;
  cdmoName: string;
  filters: MatchingFilters;
  sponsorCompanyName?: string;
  companyStage?: string;
  settings: SharedFieldSettings;
  onToggle: (key: WarmIntroShareFieldKey, checked: boolean) => void;
  onCancel: () => void;
  onSend: () => void;
  onSendAnyway: () => void;
  sending: boolean;
}

interface ShareFieldRowConfig {
  key: WarmIntroShareFieldKey;
  label: string;
  value: string;
}

const formatList = (values: string[]) => (values.length > 0 ? values.join(', ') : 'Not selected');

/**
 * Builds recommended and optional share rows from matching filters.
 *
 * @param filters - Current sponsor matching filter state
 * @param sponsorCompanyName - Sponsor company from profile
 * @param companyStage - Optional account company stage
 * @returns Field rows grouped for the review modal sections
 */
export function buildReviewShareFieldSections(
  filters: MatchingFilters,
  sponsorCompanyName: string | undefined,
  companyStage: string | undefined
): { recommended: ShareFieldRowConfig[]; optional: ShareFieldRowConfig[] } {
  const recommended: ShareFieldRowConfig[] = [
    {
      key: 'productTypes',
      label: 'What are you developing?',
      value: formatList(filters.productType.map(getPrimaryCapabilityLabel)),
    },
    {
      key: 'specificServices',
      label: 'CDMO capabilities / expertise',
      value: formatList(filters.detailedServices),
    },
    {
      key: 'requiredCertifications',
      label: 'Regulatory / compliance requirements',
      value: formatList(filters.certifications),
    },
    {
      key: 'partnerType',
      label: 'Preferred CDMO partner type',
      value: filters.cdmoPartnerType || 'Not selected',
    },
    {
      key: 'developmentStage',
      label: 'Project stage',
      value: filters.developmentStage || 'Not selected',
    },
    {
      key: 'supportNeeds',
      label: 'Support needed',
      value: formatList(filters.supportType),
    },
    {
      key: 'timeline',
      label: 'Timeline',
      value: filters.speedToStart || 'Not selected',
    },
    {
      key: 'batchSizeRange',
      label: 'Batch size / project scale',
      value: filters.batchSize || 'Not selected',
    },
  ];

  const optional: ShareFieldRowConfig[] = [
    {
      key: 'budgetRange',
      label: 'Estimated project budget',
      value: filters.budget || 'Not selected',
    },
    {
      key: 'preferredRegions',
      label: 'Preferred locations',
      value: formatList(filters.location.filter((region) => region !== 'No preference')),
    },
    {
      key: 'companyName',
      label: 'Sponsor company name',
      value: sponsorCompanyName?.trim() || 'Anonymous Sponsor',
    },
  ];

  if (companyStage?.trim()) {
    optional.push({
      key: 'accountCompanyStage',
      label: 'Company stage',
      value: companyStage,
    });
  }

  return { recommended, optional };
}

const ReviewProjectInfoModal: React.FC<ReviewProjectInfoModalProps> = ({
  isOpen,
  cdmoName,
  filters,
  sponsorCompanyName,
  companyStage,
  settings,
  onToggle,
  onCancel,
  onSend,
  onSendAnyway,
  sending,
}) => {
  const { recommended, optional } = useMemo(
    () => buildReviewShareFieldSections(filters, sponsorCompanyName, companyStage),
    [filters, sponsorCompanyName, companyStage]
  );

  const shouldWarn = countHiddenRecommendedFields(settings) >= 3;

  if (!isOpen) {
    return null;
  }

  const handleSend = () => {
    if (shouldWarn) {
      onSendAnyway();
    } else {
      onSend();
    }
  };

  return (
    <div className="pcn-modal-overlay" role="presentation">
      <div
        className="pcn-review-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-project-info-title"
      >
        <button
          className="pcn-modal-close"
          type="button"
          onClick={onCancel}
          disabled={sending}
          aria-label="Close review modal"
        >
          ×
        </button>

        <div className="pcn-modal-header">
          <div className="pcn-modal-header-top">
            <div className="pcn-modal-icon" aria-hidden="true">
              <BuildingIcon />
            </div>

            <div className="pcn-modal-header-copy">
              <h2 id="review-project-info-title">Review project info before sending</h2>

              <p className="pcn-modal-subline">
                Introduction to: <span>{cdmoName}</span>
              </p>
            </div>
          </div>

          <p className="pcn-modal-instructions">
            Choose which project details to share with this CDMO. Keep checked what you want sent,
            and uncheck anything you&apos;d prefer to keep private.
          </p>

          <p className="pcn-modal-note">
            <InfoIcon />
            Recommended fields help the CDMO assess fit faster.
          </p>
        </div>

        <div className="pcn-section">
          <h3>Recommended to Share</h3>
          <div className="pcn-field-list">
            {recommended.map((field) => (
              <ShareFieldRow
                key={field.key}
                field={field}
                checked={settings[field.key]}
                disabled={sending}
                onToggle={(key) => onToggle(key, !settings[key])}
              />
            ))}
          </div>
        </div>

        <div className="pcn-section pcn-section-optional">
          <h3>Optional / Sensitive</h3>
          <div className="pcn-field-list">
            {optional.map((field) => (
              <ShareFieldRow
                key={field.key}
                field={field}
                checked={settings[field.key]}
                disabled={sending}
                onToggle={(key) => onToggle(key, !settings[key])}
              />
            ))}
          </div>
        </div>

        {shouldWarn && (
          <div className="pcn-warning-banner" role="alert">
            <strong>Several key fit fields are hidden</strong>
            CDMOs may have difficulty evaluating this request. We recommend sharing product type,
            services, development stage, support needed, timeline, and preferred region when possible.
          </div>
        )}

        <div className="pcn-modal-footer">
          <button className="pcn-btn pcn-btn-secondary" type="button" onClick={onCancel} disabled={sending}>
            Cancel
          </button>

          <button className="pcn-btn pcn-btn-primary" type="button" onClick={handleSend} disabled={sending}>
            <SendIcon />
            {sending ? 'Sending...' : shouldWarn ? 'Send Anyway' : 'Send Intro Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

function ShareFieldRow({
  field,
  checked,
  disabled,
  onToggle,
}: {
  field: ShareFieldRowConfig;
  checked: boolean;
  disabled: boolean;
  onToggle: (key: WarmIntroShareFieldKey) => void;
}) {
  return (
    <label className="pcn-share-row">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(field.key)}
        aria-label={`Share ${field.label}`}
      />

      <span className="pcn-custom-checkbox" aria-hidden="true">
        {checked ? '✓' : ''}
      </span>

      <span className="pcn-share-label">{field.label}</span>

      <span className="pcn-share-value">{field.value}</span>
    </label>
  );
}

function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 21h16M7 21V5.5A1.5 1.5 0 0 1 8.5 4h7A1.5 1.5 0 0 1 17 5.5V21M9.5 8h1M13.5 8h1M9.5 11.5h1M13.5 11.5h1M9.5 15h1M13.5 15h1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 9v4.2M10 6.5h.01"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M21 3 10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5 21 3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default ReviewProjectInfoModal;
